import { AllCMSPageFields } from '@/cms/fields/mappers/common'
import { CatalogueType } from '@/scopes/common/domain/enums/CatalogueType'
import { getCatalogueTypeByPublicationCode } from '@/scopes/common/domain/functions/catalogues'
import { Configuration } from '@/scopes/common/domain/types/configuration'
import { MetaDescriptionResolver } from '@/scopes/common/domain/types/meta-data'
import { IntlShape } from 'react-intl'

interface ProductMetaDescriptionResolverDependencies {
  config: Configuration
  cmsFields: AllCMSPageFields
  intl: IntlShape
}

const PAGE_DESCRIPTION_TRANSLATION_KEY: Record<Exclude<CatalogueType, CatalogueType.None>, string> = {
  [CatalogueType.Summer]: 'product_default_page_description_summer',
  [CatalogueType.Winter]: 'product_default_page_description_winter',
}

function bySeasonTranslationKey(catalogueType: Exclude<CatalogueType, CatalogueType.None>) {
  return PAGE_DESCRIPTION_TRANSLATION_KEY[catalogueType]
}

function joinWithConjunction(items: string[], intl: IntlShape): string {
  return intl.formatList(items, { type: 'conjunction', style: 'long' }) as string
}

export function createCmsProductMetaDescriptionResolver({
  config,
  cmsFields,
  intl,
}: ProductMetaDescriptionResolverDependencies): MetaDescriptionResolver {
  return {
    resolve: () => {
      const catalogueType = getCatalogueTypeByPublicationCode(config.publicationCode)

      if (catalogueType === CatalogueType.None) {
        return undefined
      }

      const accommodationName = cmsFields['PDP.title']

      if (!accommodationName) {
        return undefined
      }

      const usps = cmsFields['PDP.usps']
      const brandName = config.brandName.charAt(0).toUpperCase() + config.brandName.slice(1)
      const accommodationUsps = usps?.length ? joinWithConjunction(usps, intl) : ''

      const translationKey = bySeasonTranslationKey(catalogueType)

      const description = intl.formatMessage(
        { id: translationKey, defaultMessage: '' },
        { accommodationName, accommodationUsps, brandName },
      )

      return description || undefined
    },
  }
}
